import { PlusOutlined } from '@ant-design/icons';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FormItem, useRelationEditor } from '@framework/form';
import { Button, Input, Space } from 'antd';
import React, { useCallback, useState } from 'react';
import { useBasicDndDragger } from '../../../../../../ui/dnd/hooks/useBasicDndDragger';
import { parseAdvancedRichTextToString } from '../../../../../../validator';
import { IWidgetOption } from '../../../../Widget';
import { ImportCsvOptionsButton } from './ImportCsvOptionsButton';
import { SortableWidgetOptionItem } from './SortableWidgetOptionItem';
import { IWidgetCsvConfig } from './WidgetCsvField';
import { WidgetOptionDividerEditDrawer } from './WidgetOptionDividerEditDrawer';
import { WidgetOptionValueEditDrawer } from './WidgetOptionValueEditDrawer';
import { WidgetOptionView } from './WidgetOptionView';
import { createDefaultOptionValue } from './util/createDefaultOptionValue';

export interface IWidgetOptionItemListProps {
  name: string;
}

/**
 * Componente con D'n'D per la configurazione della lista delle opzioni di widget checkbox/radio/select
 */
export function WidgetOptionItemList(props: IWidgetOptionItemListProps) {
  const { name } = props;

  const editor = useRelationEditor<IWidgetOption>(name);
  const [index, setIndex] = useState<number | null>(null);
  const { sensors, handleDragStart, handleDragEnd, itemIds, active } =
    useBasicDndDragger(editor);

  const sortyByLabel = useCallback(() => {
    editor.replace(
      editor.items.sort((a, b) => {
        const aLabel =
          a.type === 'divider' ? '' : parseAdvancedRichTextToString(a.label);
        const bLabel =
          b.type === 'divider' ? '' : parseAdvancedRichTextToString(b.label);
        return aLabel.localeCompare(bLabel);
      }),
    );
  }, [editor]);

  const sortByValue = useCallback(() => {
    editor.replace(
      editor.items.sort((a, b) => {
        const aValue = a.type === 'divider' ? '' : a.value;
        const bValue = b.type === 'divider' ? '' : b.value;
        return aValue.localeCompare(bValue);
      }),
    );
  }, [editor]);

  // Funzione per gestire l'importazione delle opzioni
  const handleImport = (config: IWidgetCsvConfig) => {
    // Sostiuisce le opzioni attuali con quelle importate
    editor.replace(
      config.combinations.map(combination => ({
        type: 'option',
        label: [
          {
            text: combination.values[0],
            type: 'text',
            rule: null,
          },
        ],
        value: combination.values[1],
        visible: { type: 'static', value: true },
        enabled: { type: 'static', value: true },
      })),
    );
  };

  return (
    <>
      <Space>
        <Button onClick={sortyByLabel}>Ordina per Etichetta</Button>
        <Button onClick={sortByValue}>Ordina per Valore</Button>
      </Space>
      <FormItem name={name} label={null}>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={itemIds}
            strategy={verticalListSortingStrategy}
          >
            {editor.items.map((item, index) => (
              <SortableWidgetOptionItem
                item={item}
                uid={item.__uid!}
                type={item.type}
                key={item.__uid}
                active={active}
                onEdit={e => {
                  editor.handle.open(item);
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onRemove={e => {
                  editor.remove(item);
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onAdd={e => {
                  setIndex(index + 1);
                  editor.handle.open(createDefaultOptionValue());

                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
            ))}
            <DragOverlay>
              {active && <WidgetOptionView item={active} />}
            </DragOverlay>
          </SortableContext>
        </DndContext>

        {/* Pulsanti da aggiunta */}
        <Input.Group compact>
          <WidgetOptionValueEditDrawer
            editor={editor}
            index={index}
            setIndex={setIndex}
            buttonProps={{
              type: 'dashed',
              size: 'large',
              icon: <PlusOutlined aria-hidden="true" />,
              style: { width: '50%' },
              children: 'Aggiungi Opzione',
            }}
          />
          <WidgetOptionDividerEditDrawer
            editor={editor}
            buttonProps={{
              type: 'dashed',
              size: 'large',
              icon: <PlusOutlined aria-hidden="true" />,
              style: { width: '50%' },
              children: 'Aggiungi Separatore',
            }}
          />
        </Input.Group>
        {/* Pulsante per import da file csv */}
        <ImportCsvOptionsButton onImport={handleImport} />
      </FormItem>
    </>
  );
}
